<template lang="pug">
//- section.abonements

section.abonements
    el-row.no-margin(:gutter="20")
        el-col(:sm="10")
            .flex-row
                el-form-item.grow(:label="$t('Name')" prop="name")
                    el-input(v-model="name" maxlength="255" show-word-limit)

                //- Color indicators section for super users

                el-form-item(v-if="isSuperUser" label=" ")
                    nobr.indicators.el-loading-parent--relative
                        .spinner(v-if="indicatorOptionsLoading")
                        a.indicator.is-outlined(
                            v-else
                            v-for="indicator in indicatorOptions"
                            :class="[ indicator.type, { active: hasIndicator(indicator) } ]"
                            href="#"
                            @click.prevent.stop="toggleIndicator(indicator)"
                        )
                            i.el-icon-fa-check.fas(v-if="hasIndicator(indicator)")
            //- The handle this website is addressed by from outside: it becomes
            //- the local part of the address a customer can email to open a
            //- ticket against this account. Ours to hand out - the API drops the
            //- write for anyone who is not a super user.
            el-form-item(v-if="isSuperUser" :label="$t('Ticket key')")
                el-input(:value="key" @input="onKeyInput")
                .help(v-if="intakeAddress")
                    | {{ $t('Mail to this address opens a ticket for this website:') }}
                    br
                    intake-address(:address="intakeAddress")
                .help(v-else) {{ $t('Used to build the address this website can be reached at by email.') }}

            el-form-item(:label="$t('Template')" prop="config.template")
                el-select.is-full-width(
                    placeholder=""
                    filterable
                    v-model="template"
                    valueKey="id"
                    :class="{ 'loading': options.template.loading }"
                )
                    el-option(v-for="(item, key) in options.template.items" :key="item.id" :label="item.name" :value="item")

            el-form-item.mb-6
                el-switch(
                    :active-text="$t('Track email opens and clicks')"
                    v-model="emailTrackingEnabled"
                )
                .help {{ $t('Adds an invisible pixel and wraps links to measure engagement on emails sent to customers.') }}

            el-form-item(:label="$t('Logo')")
                el-upload.plus-box(
                    v-if="! logo"
                    ref="upload"
                    drag multiple :action="uploadEndpoint"
                    accept="image/jpg, image/jpeg, image/png, image/svg+xml"
                    :before-upload="validateUpload"
                    :on-change="updateUploadHeaders"
                    :on-success="uploadSucceed"
                    :on-error="uploadFailed"
                    :headers="uploadHeaders"
                    :data="uploadData"
                )
                    i.el-icon-plus
                    // i.el-icon-upload.el-icon-fa-cloud-upload-alt.fas
                    .el-upload__text(v-html="$t('upload.drop.plural')")
                    .el-upload__tip(slot="tip") {{ $t('upload.validation', { filetypes: 'jpg/png', size: '3mb' }) }}
                .logo-input(v-else :class="{ deleted: logo.deleted }")
                    img(:src="src")
                    el-button.restore-image(
                        v-if="logo.deleted"
                        circle type="success" size="mini"
                        icon="el-icon-fa-trash-restore fas"
                        @click="restoreLogo"
                    )
                    el-button.delete-image(
                        v-else
                        circle type="info" size="mini"
                        icon="el-icon-fa-trash-alt far"
                        @click="deleteLogo"
                    )
            template(v-if="i18nFeatureEnabled")
                hr.wave
                LocalizationSettings(
                    :i18n="i18n"
                    :languages="languages"
                    @update:i18n="onI18nChange"
                    @update:languages="onLanguagesChange"
                )

        el-col(:sm="14")
            el-form-item(:label="$t('Webspace ID')" prop="id")
                el-input(:value="data.id" :disabled="true")

            .flex-row(v-if="extensionAvailable('restricted-area') || extensionAvailable('shop')")
                el-form-item(label=" ")
                    el-switch(
                        :active-text="$t('Assign tags to new registrations')"
                        v-model="assignMemberTags"
                    )
                el-form-item.grow(:label="$t('Tags')" prop="assignableMemberTags")
                    el-select.is-full-width(
                        v-model="assignableMemberTags"
                        placeholder=""
                        valueKey="id"
                        multiple
                        filterable
                        clearable
                        allow-create
                        default-first-option
                        :disabled="!assignMemberTags"
                        :class="{ 'loading': options.memberTag.loading }"
                    )
                        el-option(
                            v-for="item in memberTagOptions"
                            :key="item.id"
                            :label="item.name"
                            :value="item"
                        )

            .block.domains
                Domain(
                    v-for="(domain, i) in domains" :key="i" :index="i"
                    :deletable="domains.length > 1"
                    :data="domain"
                    :guest-eligible-groups="guestEligibleGroups"
                )
                el-button(type="primary" size="mini" icon="el-icon-fa-plus fas" @click="addDomain") {{ $t('Add Domain') }}

    //- Blueprint section for super users (not shown for business card CMSs)

    section.blueprint-admin(v-if="isSuperUser")
        el-form-item.no-margin(label=" ")
            el-switch(
                :active-text="$t('Use as Blueprint')"
                v-model="isBlueprint"
            )
        transition(name="slide-decent-vertical")
            .blueprint-elements(v-if="isBlueprint")
                el-row(:gutter="20")
                    el-col(:sm="10")
                        el-form-item(:label="$t('Blueprint Name')")
                            el-input(v-model="blueprintName" maxlength="255" :placeholder="$t('Display name in wizard')")
                    el-col(:sm="14")
                        el-form-item(:label="$t('Preview URL')")
                            el-input(v-model="blueprintUrl" :placeholder="$t('https://example.com')")
                el-row(:gutter="20")
                    el-col(:sm="24")
                        el-form-item(:label="$t('Blueprint Description')")
                            quill-editor(
                                :placeholder="$t('Short description of this blueprint')"
                                :options="quillConfig"
                                v-model="blueprintDescription"
                            )
                el-row(:gutter="20")
                    el-col(:sm="24")
                        el-form-item(:label="$t('Screenshots')")
                            .screenshot-gallery
                                draggable(
                                    :value="activeScreenshots"
                                    @input="onScreenshotsReorder"
                                    tag="div"
                                    class="screenshot-items"
                                )
                                    .screenshot-item(
                                        v-for="(screenshot, index) in activeScreenshots"
                                        :key="screenshot.id"
                                    )
                                        img(:src="getScreenshotSrc(screenshot)" :width="screenshot.width" :height="screenshot.height")
                                        el-button.remove-screenshot(
                                            circle
                                            type="info"
                                            size="mini"
                                            icon="el-icon-fa-trash-alt far"
                                            @click="removeScreenshot(index)"
                                        )
                                el-upload.plus-box.screenshot-upload(
                                    ref="screenshotUpload"
                                    drag
                                    multiple
                                    :action="uploadEndpoint"
                                    accept="image/jpg, image/jpeg, image/png"
                                    :before-upload="validateUpload"
                                    :on-change="updateUploadHeaders"
                                    :on-success="screenshotUploadSucceed"
                                    :on-error="uploadFailed"
                                    :headers="uploadHeaders"
                                    :data="screenshotUploadData"
                                    :show-file-list="false"
                                )
                                    i.el-icon-plus
                                    .el-upload__text(v-html="$t('upload.drop.plural')")
                                    .el-upload__tip(slot="tip") {{ $t('upload.validation', { filetypes: 'jpg/png', size: '3mb' }) }}

</template>

<i18n>
{
    "de": {
        "Ticket key": "Ticket-Schlüssel",
        "Mail to this address opens a ticket for this website:": "E-Mails an diese Adresse öffnen ein Ticket für diesen Webauftritt:",
        "Used to build the address this website can be reached at by email.": "Daraus wird die E-Mail-Adresse gebildet, über die dieser Webauftritt erreichbar ist.",
        "Name": "Name",
        "Tagline": "Tagline",
        "Cms": "Cms",
        "Template": "Template",
        "Assign tags to new registrations": "Neue Registrierungen taggen",
        "Track email opens and clicks": "E-Mail-Öffnungen und Klicks erfassen",
        "Adds an invisible pixel and wraps links to measure engagement on emails sent to customers.": "Fügt einen unsichtbaren Zählpixel ein und leitet Links um, um die Interaktion mit E-Mails an Kunden zu messen.",
        "Domains": "Domains",
        "Domain": "Domain",
        "CMS related settings": "Webauftrittbezogene Einstellungen",
        "Shop name": "Shopname",
        "Contact details": "Kontaktdaten",
        "Add Domain": "Domain hinzufügen",
        "Blueprint Settings": "Blaupausen-Einstellungen",
        "Use as Blueprint": "Als Blaupause verwenden",
        "Business Card Only": "Nur Visitenkarte",
        "Blueprint Name": "Blaupausen-Name",
        "Display name in wizard": "Anzeigename im Assistenten",
        "Preview URL": "Vorschau-URL",
        "Blueprint Description": "Blaupausen-Beschreibung",
        "Short description of this blueprint": "Kurze Beschreibung dieser Blaupause",
        "Color indicators": "Farbmarker",
        "Screenshots": "Screenshots",
        "Add Screenshot": "Screenshot hinzufügen",
        "Crop Image": "Bild zuschneiden",
        "Preview": "Vorschau",
        "Apply": "Anwenden",
        "Cancel": "Abbrechen"
    }
}
</i18n>

<script>
import slugify from 'slugify';
import _ from 'lodash'
import uniqid from 'uniqid'

import router from '@/router'
import { ensureFreshToken } from '@/helpers'

import AuthApi from '@/store/api/system/auth';
import IndicatorApi from '@/store/api/system/indicator';
import quillConfig from '@/quill/config'
import { useAuthStore, useSettingsStore, useSetupStore } from '@/stores'

import MapBoxKey from './Partials/MapBoxKey'
import GoogleRecaptchaKeys from './Partials/GoogleRecaptchaKeys'
import SmtpCredentials from './Partials/SmtpCredentials'
import PayPalKeys from './Partials/PayPalKeys'
import Domain from './Partials/Domain'

import AddressCard from '@/components/AddressCard'
import LocalizationSettings from '@/components/LocalizationSettings'
import IntakeAddress from '@/extensions/tickets/components/IntakeAddress'
import draggable from 'vuedraggable'

export default {
    name: 'AbonementBasic',
    data() {
        return {
            quillConfig,
            indicatorOptionsLoading: false,
            indicatorOptions: [],
        }
    },
    components: {
        Domain,
        MapBoxKey,
        GoogleRecaptchaKeys,
        SmtpCredentials,
        PayPalKeys,
        AddressCard,
        LocalizationSettings,
        draggable,
        'intake-address': IntakeAddress,
    },
    computed: {
        uploadHeaders() {
            return {
                authorization: `Bearer ${useAuthStore().token}`,
                accept: 'application/json',
            }
        },
        i18nFeatureEnabled() {
            let features = _.get(this.template, 'features') || [];
            return !!features.find(el => el === 'i18n');
        },
        nr: {
            get() {
                return _.get(this.data, 'nr', '')
            },
            set(value) {
                useSettingsStore().setFormNr(value)
            }
        },
        key() {
            return _.get(this.data, 'key', '')
        },
        intakeAddress() {
            return _.get(this.data, 'intake_address', '')
        },
        name: {
            get() {
                return _.get(this.data, 'name', '')
            },
            set(value) {
                useSettingsStore().setFormName(value)
            }
        },
        sub_title: {
            get() {
                return _.get(this.data, 'sub_title', '')
            },
            set(value) {
                useSettingsStore().setFormSubTitle(value)
            }
        },
        template: {
            get() {
                return _.get(this.data, 'config.template')
            },
            set(value) {
                useSettingsStore().setFormTemplate(value);
            }
        },
        assignMemberTags: {
            get() {
                return _.get(this.data, 'assign_member_tags', false);
            },
            set(value) {
                useSettingsStore().setFormAssignMemberTags(value);
            },
        },
        emailTrackingEnabled: {
            get() {
                // defaults to true to mirror the backend (Cancellation::shouldTrackEngagement)
                return _.get(this.data, 'email_tracking_enabled', true);
            },
            set(value) {
                useSettingsStore().setFormEmailTrackingEnabled(value);
            },
        },
        assignableMemberTags: {
            get() {
                let tags = _.get(this.data, 'assignableMemberTags') || [];
                return tags.map(tag => {
                    if (tag.id.startsWith('new:')) return tag.name
                    else return tag
                })
            },
            set(value) {
                let memberTags = []
                value.forEach(tag => {
                    if (typeof tag === 'string' || tag instanceof String) {
                        memberTags.push({
                            id: uniqid('new:'),
                            name: tag,
                        })
                    } else {
                        memberTags.push(tag)
                    }
                })
                useSettingsStore().setFormAssignableMemberTags(memberTags);
            },
        },
        memberTagOptions() {
            let items = this.options.memberTag.items || [];
            const itemIDs = items.map(item => item.id);

            this.assignableMemberTags.forEach(tag => {
                // || tag.id.startsWith('new:')
                if (!itemIDs.includes(tag.id)) {
                    items.push(tag);
                }
            });

            return items;
        },
        extensions() {
            return _.get(this.template, 'extensions') || []
        },
        logo() {
            return _.find(this.data.images, { type: 'cms-logo' })
        },
        // images: () => console.log(this.data), // _.get(account.state.form, 'images', []),

        domains() {
            return _.get(this.data, 'domains') || []
        },
        uploadData() {
            let uploadData = {
                'context[type]': 'cms-logo'
            }
            if (this.data.id) {
                uploadData = {
                    'context[type]': 'cms-logo',
                    'context[model]': 'CityPages\\Cms',
                    'context[id]': this.data.id
                }
            }

            return uploadData
        },
        src() {
            if (this.logo) {
                const storageRoute = _.trim(`${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PRIVATE_STORAGE_ROUTE}`, '/')
                let table = 'images'
                if (this.logo.upload) table = 'uploads'

                const token = AuthApi.getToken()
                return `${storageRoute}/${table}/${this.logo.id}/${this.logo.name}?token=${token}`
            }
        },
        i18n() {
            return _.get(this.data, 'i18n')
        },
        languages() {
            const languages = _.get(this.data, 'languages') || []
            return _.cloneDeep(languages).sort((a, b) => a.position - b.position)
        },
        isBlueprint: {
            get() {
                return _.get(this.data, 'is_blueprint', false)
            },
            set(value) {
                useSettingsStore().setFormIsBlueprint(value)
            }
        },
        blueprintName: {
            get() {
                return _.get(this.data, 'blueprint_name', '')
            },
            set(value) {
                useSettingsStore().setFormBlueprintName(value)
            }
        },
        blueprintDescription: {
            get() {
                return _.get(this.data, 'blueprint_description', '')
            },
            set(value) {
                useSettingsStore().setFormBlueprintDescription(value)
            }
        },
        blueprintUrl: {
            get() {
                return _.get(this.data, 'blueprint_url', '')
            },
            set(value) {
                useSettingsStore().setFormBlueprintUrl(value)
            }
        },
        blueprintScreenshots() {
            return _.get(this.data, 'blueprintScreenshots') || []
        },
        activeScreenshots() {
            return this.blueprintScreenshots
                .filter(screenshot => !screenshot.deleted)
                .sort((a, b) => (a.position || 0) - (b.position || 0))
        },
        screenshotUploadData() {
            let uploadData = {
                'context[type]': 'blueprint-screenshot'
            }
            if (this.data.id) {
                uploadData = {
                    'context[type]': 'blueprint-screenshot',
                    'context[model]': 'CityPages\\Cms',
                    'context[id]': this.data.id
                }
            }
            return uploadData
        },
        isSuperUser() {
            return useAuthStore().isSuperUser;
        },
        data() {
            return useSettingsStore().form;
        },
        options() {
            return useSettingsStore().options;
        },
        uploadEndpoint() {
            return useSetupStore().uploadEndpoint;
        },
        activeCms() {
            return useSetupStore().activeCms;
        },
        locale() {
            return useSetupStore().locale;
        },
        cmss() {
            return useSetupStore().cmss;
        },
        // alternative-guest-customer-groups: read straight from the activeCms's
        // shop. The CmsShop fragment in Cms.gql already projects
        // `shop.customerGroups`, so no extra fetch is needed — this stays
        // consistent with the rest of the activeCms-driven state.
        guestEligibleGroups() {
            const groups = _.get(this.activeCms, 'shop.customerGroups') || [];
            return groups.filter(group => group.guest_eligible);
        },
    },
    methods: {
        // The key ends up in an email local part, so it is lowercase, ASCII and
        // hyphenated. Slugified as it is typed, so what is shown is what the
        // API will store - its rule would otherwise reject a typed space.
        onKeyInput(value) {
            useSettingsStore().setFormKey(
                slugify(String(value || ''), { lower: true, strict: true, locale: 'de' }).slice(0, 32)
            );
        },
        onI18nChange(value) {
            useSettingsStore().setFormI18n(value)
        },
        onLanguagesChange(value) {
            useSettingsStore().setFormLanguages(value.map((language, i) => {
                language.position = i + 1
                if (language.status === undefined) language.status = 'disabled'
                return language
            }))
        },
        addAbonement() {
            useSettingsStore().reset();
            useSettingsStore().setFormInitialized(false);
            const params = { id: 'new' }
            router.push({ name: 'settings-form', params })
            // router.push({ name: 'settings-basic', params })
        },
        deleteLogo() {
            const index = _.findIndex(this.data.images, { type: 'cms-logo' })
            if (index >= 0) {
                useSettingsStore().markFormImageAsDeleted({ index })
            }
        },
        restoreLogo() {
            const index = _.findIndex(this.data.images, { type: 'cms-logo' })
            if (index >= 0) {
                useSettingsStore().unmarkFormImageAsDeleted({ index })
            }
        },
        addDomain() {
            useSettingsStore().addFormDomain()
        },
        async updateUploadHeaders() {
            // The computed uploadHeaders reads the store token; make sure it is fresh.
            await ensureFreshToken()
        },
        validateUpload(file) {
            return new Promise(async (resolve, reject) => {
                await this.updateUploadHeaders()
                if (file.size / 1024 / 1024 > 3) {
                    this.$notify.error({
                        title: this.$t('upload.sizeExceeded'),
                        message: this.$t('upload.max', { size: '3mb' }),
                        iconClass: 'fas el-icon-fa-xmark',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    })
                    return reject('too large')
                }
                resolve()
            })
        },
        async uploadSucceed(response, file) {
            let image = response
            image.upload = true

            if (typeof image.size === 'number') image.size = image.size.toString()
            delete image.hash
            delete image.dimensions

            setTimeout(() => {
                if (this.$refs.upload) {
                    this.$refs.upload.uploadFiles = this.$refs.upload.uploadFiles.filter(el => el.uid !== file.uid)
                }
            }, 1000)

            useSettingsStore().addFormImage({ image })
            await this.updateUploadHeaders()
        },
        async uploadFailed(err, file) {
            await this.updateUploadHeaders()

            this.$notify.error({
                title: this.$t('upload.notUploaded'),
                message: _.get(file, 'name'),
                iconClass: 'fas el-icon-fa-xmark',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000,
            })
        },
        extensionAvailable(extension) {
            return this.extensions.includes(extension)
        },
        getScreenshotSrc(screenshot) {
            const storageRoute = _.trim(`${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PRIVATE_STORAGE_ROUTE}`, '/')
            let table = 'images'
            if (screenshot.upload) table = 'uploads'
            const token = AuthApi.getToken()
            return `${storageRoute}/${table}/${screenshot.id}/${screenshot.name}?token=${token}`
        },
        removeScreenshot(activeIndex) {
            // Find the actual index in the full array (including deleted items)
            const screenshot = this.activeScreenshots[activeIndex]
            const realIndex = this.blueprintScreenshots.findIndex(s => s.id === screenshot.id)
            if (realIndex >= 0) {
                useSettingsStore().removeFormBlueprintScreenshot({ index: realIndex })
            }
        },
        onScreenshotsReorder(reorderedScreenshots) {
            useSettingsStore().reorderFormBlueprintScreenshots(reorderedScreenshots)
        },
        async screenshotUploadSucceed(response, file) {
            let image = response
            image.upload = true
            image.type = 'blueprint-screenshot'

            // use dimensions to create a skeleton quicker than onload
            image.width = response.dimensions.width
            image.height = response.dimensions.height

            if (typeof image.size === 'number') image.size = image.size.toString()
            delete image.hash
            delete image.dimensions

            setTimeout(() => {
                if (this.$refs.screenshotUpload) {
                    this.$refs.screenshotUpload.uploadFiles = this.$refs.screenshotUpload.uploadFiles.filter(el => el.uid !== file.uid)
                }
            }, 1000)

            useSettingsStore().addFormBlueprintScreenshot({ image })
            await this.updateUploadHeaders()
        },
        hasIndicator(indicator) {
            var indicators = _.get(this.data, 'indicators') || [];
            return !!indicators.find(function(element) { return element.id === indicator.id; });
        },
        toggleIndicator(indicator) {
            var indicators = _.cloneDeep(_.get(this.data, 'indicators') || []);
            var index = indicators.findIndex(function(element) { return element.id === indicator.id; });
            if (index >= 0) {
                indicators.splice(index, 1);
            } else {
                indicators.push(indicator);
            }
            useSettingsStore().setFormIndicators(indicators);
        },
        fetchIndicatorOptions() {
            var self = this;
            self.indicatorOptionsLoading = true;
            IndicatorApi.fetchList()
                .then(function(indicators) {
                    self.indicatorOptionsLoading = false;
                    self.indicatorOptions = indicators;
                })
                .catch(function() {
                    self.indicatorOptionsLoading = false;
                });
        },
    },
    mounted() {
        if (this.isSuperUser) {
            this.fetchIndicatorOptions();
        }
    }
}
</script>

<style lang="sass" scoped>
.logo-input
    display: inline-block
    position: relative
    & > img
        max-height: 120px
        max-width: 100%
        border-radius: 3px
        transition: .15s filter ease, .15s opacity ease
    .el-button.delete-image,
    .el-button.restore-image
        position: absolute
        top: 5px
        right: 5px
        z-index: 2
        width: 30px
        height: 30px
        opacity: 0
        -webkit-transition: opacity .15s ease
        transition: opacity .15s ease
    &:hover
        .el-button.delete-image,
        .el-button.restore-image
            opacity: 1
    &.deleted
        & > img
            opacity: .6
            filter: grayscale(1) blur(1px)

.wave
    margin: 1rem 0

.blueprint-admin
    .section-title
        margin: 0 0 15px
        font-size: 16px
        font-weight: 500

.blueprint-elements
    background: #f5f7fa
    border-radius: 8px
    padding: 20px
    margin-top: 4px

.screenshot-gallery
    display: flex
    flex-wrap: wrap
    gap: 12px
    align-items: stretch

.screenshot-items
    display: contents

.screenshot-item
    position: relative
    flex-shrink: 0
    border-radius: 4px
    overflow: hidden
    border: 1px solid #dcdfe6

    img
        display: block
        height: 167px
        width: auto
        border-radius: 3px
        background: #f5f7fa

    .remove-screenshot
        position: absolute
        top: 5px
        right: 5px
        z-index: 2
        width: 30px
        height: 30px
        opacity: 0
        transition: opacity 0.15s ease

    &:hover .remove-screenshot
        opacity: 1

.screenshot-upload
    flex-grow: 1
    ::v-deep .el-upload
        width: 100%
    ::v-deep .el-upload-dragger
        width: 100%
        height: 150px
        display: flex
        flex-direction: column
        justify-content: center
        align-items: center

// Image actions on hover
.logo-input
    .image-actions
        position: absolute
        top: 5px
        right: 5px
        display: flex
        gap: 4px
        opacity: 0
        transition: opacity 0.15s ease
        z-index: 2

        .el-button
            width: 30px
            height: 30px

    &:hover .image-actions
        opacity: 1

.indicators
    display: inline-flex
    gap: 2px

.indicator
    position: relative
    width: 13px
    height: 13px
    transform: scale(1.2)
    i.fas
        position: absolute
        left: calc(50% - 0.45em)
        top: calc(50% - 0.45em)
        color: white
        font-size: 0.6em

.help
    display: block
    margin-top: 4px
    font-size: 12px
    color: var(--color-text-muted, #909399)
    // mandatory: el-form-item__content inherits a 36px line-height otherwise
    line-height: 1.4

</style>
